import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Search, Star, Clock, ChevronDown, ChevronRight, Sparkles, X, ArrowRight, Percent, Tag, Flame, Timer, RotateCcw, Heart, Plus,
} from "lucide-react";
import BannerCarousel from "@/components/customer/BannerCarousel";
import VoiceSearchButton from "@/components/customer/VoiceSearchButton";
import { useCart } from "@/contexts/CartContext";
import { isRestaurantOpen, distanceMiles, formatMiles } from "@/lib/restaurantUtils";

const CATEGORY_IMAGES = {
  Caribbean: "https://images.pexels.com/photos/27556985/pexels-photo-27556985.jpeg",
  BBQ: "https://images.unsplash.com/photo-1544025162-d76694265947?w=400",
  Seafood: "https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400",
  Burgers: "https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=400",
  Pizza: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=400",
  Chinese: "https://images.unsplash.com/photo-1552611052-33e04de081de?w=400",
  Indian: "https://images.unsplash.com/photo-1631292784640-2b24be784d5d?w=400",
  Healthy: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400",
  Breakfast: "https://images.unsplash.com/photo-1550547660-d9450f859349?w=400",
  Desserts: "https://images.unsplash.com/photo-1571115177098-24ec42ed204d?w=400",
};

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiPicks, setAiPicks] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [craving, setCraving] = useState("");
  const [recoPicks, setRecoPicks] = useState([]);
  const [recoLoading, setRecoLoading] = useState(true);
  const [recoPersonalized, setRecoPersonalized] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [lastOrder, setLastOrder] = useState(null);
  const [gps, setGps] = useState(null); // { lat, lng } from browser
  const { replaceCart, addItem } = useCart();

  // One-shot geolocation request so we can rank restaurants by real distance.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8_000 },
    );
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/restaurants").then((r) => setRestaurants(r.data)).catch(() => {}).finally(() => setLoading(false));
    api
      .get("/ai/recommendations")
      .then((r) => {
        setRecoPicks(r.data.picks || []);
        setRecoPersonalized(!!r.data.personalized);
      })
      .catch(() => {})
      .finally(() => setRecoLoading(false));
  }, [user?.user_id]);

  // Poll the customer's in-flight order so the "Track your food" banner
  // is always live — it appears the moment a purchase completes and vanishes
  // once the food is delivered.
  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/orders/mine");
        if (cancelled) return;
        const live = (data || []).find((o) => !["delivered", "cancelled"].includes(o.status));
        setActiveOrder(live || null);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.user_id]);

  // Load favorites (top ordered dishes) + last order for the Reorder shortcut.
  useEffect(() => {
    if (!user?.user_id) return;
    api.get("/orders/mine/favorites")
      .then((r) => {
        setFavorites(r.data.favorites || []);
        setLastOrder(r.data.last_order || null);
      })
      .catch(() => {});
  }, [user?.user_id]);

  // Reorder handler — copies items from the last order into cart, jumps to checkout.
  const reorderLast = () => {
    if (!lastOrder) return;
    replaceCart(
      {
        restaurant_id: lastOrder.restaurant_id,
        name: lastOrder.restaurant_name,
        image_url: lastOrder.restaurant_image,
        delivery_fee: lastOrder.delivery_fee || 3.99,
        eta_minutes: lastOrder.eta_minutes || 25,
      },
      lastOrder.items,
    );
    toast.success(`${lastOrder.items.length} item${lastOrder.items.length !== 1 ? "s" : ""} re-added from ${lastOrder.restaurant_name}`);
    navigate("/checkout");
  };

  // Add a single favourite dish to the cart (uses restaurant context from favourite).
  const addFavoriteToCart = (fav) => {
    addItem(
      {
        restaurant_id: fav.restaurant_id,
        name: fav.restaurant_name,
        image_url: fav.restaurant_image,
        delivery_fee: 3.99,
        eta_minutes: 25,
      },
      { item_id: fav.item_id, name: fav.name, price: fav.price, image: fav.image },
      1,
    );
    toast.success(`Added ${fav.name} to cart`);
  };

  // Enrich every restaurant with a `miles` value (null when no GPS). This
  // becomes the primary sort key for the discovery grid.
  const withDistance = useMemo(() => {
    return restaurants.map((r) => ({
      ...r,
      miles: gps ? distanceMiles(gps, { lat: r.lat, lng: r.lng }) : null,
      is_open: isRestaurantOpen(r),
    }));
  }, [restaurants, gps]);

  const filtered = useMemo(() => {
    let list = withDistance;
    if (category !== "All") list = list.filter((r) => r.categories?.includes(category));
    if (query) list = list.filter((r) => r.name.toLowerCase().includes(query.toLowerCase()));
    // Nearest first when we have a fix, else fall back to rating.
    if (gps) {
      list = [...list].sort((a, b) => (a.miles ?? Infinity) - (b.miles ?? Infinity));
    }
    return list;
  }, [withDistance, category, query, gps]);

  const popular = useMemo(() => [...withDistance].sort((a, b) => b.rating - a.rating).slice(0, 8), [withDistance]);
  const fastest = useMemo(() => [...withDistance].sort((a, b) => a.eta_minutes - b.eta_minutes).slice(0, 8), [withDistance]);
  const sponsored = useMemo(() => withDistance.filter((r) => r.sponsored), [withDistance]);

  const askAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/recommend", { craving: craving || "authentic Caribbean flavors" });
      setAiPicks(data.picks || []);
      if (data.picks?.length) toast.success("Fresh picks just for you!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiLoading(false); }
  };

  return (
    <div className="pb-32">
      {/* Search overlay */}
      {showSearch && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col fade-up" data-testid="search-overlay">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-neutral-100">
            <button onClick={() => { setShowSearch(false); setQuery(""); }} className="p-2 -ml-2" data-testid="close-search-btn">
              <X className="w-5 h-5" />
            </button>
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                autoFocus
                data-testid="search-overlay-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Eats GND — restaurants, cuisines, dishes"
                className="w-full pl-11 pr-14 py-3 rounded-full bg-eatup-gray outline-none focus:ring-4 focus:ring-eatup-green/10"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <VoiceSearchButton
                  onTranscript={(t) => { setQuery(t); toast.success(`Heard: "${t}"`); }}
                />
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {!query && (
              <>
                <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-3">Try these</div>
                <div className="flex flex-wrap gap-2">
                  {["jerk chicken", "curry goat", "seafood", "roti", "sorrel"].map((q) => (
                    <button key={q} onClick={() => setQuery(q)} className="pill" data-testid={`quick-search-${q}`}>
                      {q}
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="mt-6 grid gap-3">
              {filtered.map((r) => (
                <button key={r.restaurant_id} onClick={() => { setShowSearch(false); navigate(`/restaurant/${r.restaurant_id}`); }}
                  className="flex items-center gap-3 p-2 rounded-2xl hover:bg-eatup-gray transition text-left" data-testid={`search-result-${r.restaurant_id}`}>
                  <img src={r.image_url} alt="" className="w-14 h-14 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-xs text-neutral-500">{r.categories?.join(" · ")}</div>
                  </div>
                  <div className="text-xs bg-eatup-green/10 text-eatup-green px-2 py-1 rounded-full font-bold">{r.rating}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search bar row */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-6">
        <button
          data-testid="home-search-btn"
          onClick={() => setShowSearch(true)}
          className="w-full flex items-center gap-3 bg-white border border-neutral-200 hover:border-neutral-300 rounded-full px-5 py-3.5 text-left shadow-sm transition"
        >
          <Search className="w-4 h-4 text-neutral-400" />
          <span className="text-neutral-500 text-sm">Search for restaurants, cuisines, or dishes</span>
        </button>
      </div>

      {/* Live-tracking banner — only shows when the customer has an in-flight order. */}
      {activeOrder && <ActiveTrackBanner order={activeOrder} navigate={navigate} />}

      {/* Reorder last order — one-tap shortcut back to a familiar meal.
          Hidden when the user has never ordered before. */}
      {lastOrder && !activeOrder && (
        <ReorderLastBanner order={lastOrder} onReorder={reorderLast} />
      )}

      {/* Your Favorites — top 3-5 most-ordered dishes with instant Add to Cart */}
      {favorites.length > 0 && (
        <FavoritesRail favorites={favorites} onAdd={addFavoriteToCart} navigate={navigate} />
      )}

      {/* Banner ad carousel — auto-sliding sponsored promos (managed in Admin Panel) */}
      <BannerCarousel />

      {/* Hero banner strip (uber eats style: horizontally scrolling deals) */}
      <section className="mt-6 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-3 gap-4">
          <DealCard
            title="10% off first 3 orders"
            code="EATSGND10"
            color="from-eatup-green to-eatup-green-dark"
            icon={<Percent className="w-5 h-5" />}
            image="https://images.pexels.com/photos/27556985/pexels-photo-27556985.jpeg"
            testid="deal-eatsgnd10"
          />
          <DealCard
            title="Free delivery today"
            code="FREESHIP"
            color="from-eatup-charcoal to-neutral-800"
            icon={<Timer className="w-5 h-5 text-eatup-yellow" />}
            image="https://images.unsplash.com/photo-1600891964599-f61ba0e24092?w=800"
            testid="deal-freeship"
          />
          <DealCard
            title="$5 off Seafood"
            code="TROPICAL5"
            color="from-blue-600 to-blue-700"
            icon={<Tag className="w-5 h-5" />}
            image="https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=800"
            testid="deal-tropical5"
          />
        </div>
      </section>

      {/* AI-powered "Recommended for you" carousel (auto-loads, personalized when signed-in) */}
      <AiRecoRail
        picks={recoPicks}
        loading={recoLoading}
        personalized={recoPersonalized}
        navigate={navigate}
      />

      {/* Categories rail with round tiles */}
      <section className="mt-8 max-w-7xl mx-auto pl-5 sm:pl-8">
        <div className="pr-5 sm:pr-8 mb-4 flex items-baseline justify-between">
          <h2 className="font-display text-xl font-bold">What are you feeling?</h2>
          <div className="text-xs text-neutral-500">Tap to filter</div>
        </div>
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
          <CategoryTile label="All" emoji="🌈" active={category === "All"} onClick={() => setCategory("All")} testid="cat-all" />
          {categories.map((c) => (
            <CategoryTile
              key={c.key}
              label={c.key}
              emoji={c.emoji}
              image={CATEGORY_IMAGES[c.key]}
              active={category === c.key}
              onClick={() => setCategory(c.key)}
              testid={`cat-${c.key.toLowerCase()}`}
            />
          ))}
        </div>
      </section>

      {/* AI Concierge (compact) */}
      <section className="mt-8 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="rounded-3xl overflow-hidden relative bg-eatup-charcoal text-white p-6 sm:p-8">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-eatup-green/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-eatup-yellow uppercase tracking-[0.35em] text-[10px] font-bold">
              <Sparkles className="w-3.5 h-3.5" /> AI Concierge
            </div>
            <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold max-w-md">Not sure what to eat? Tell us the vibe.</h3>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <input
                data-testid="ai-craving-input"
                value={craving}
                onChange={(e) => setCraving(e.target.value)}
                placeholder='e.g. "spicy caribbean for two"'
                className="flex-1 px-4 py-3 rounded-2xl bg-white/10 border border-white/10 focus:bg-white/15 outline-none text-white placeholder:text-white/50"
              />
              <button data-testid="ai-recommend-btn" onClick={askAi} disabled={aiLoading}
                className="bg-eatup-green hover:bg-eatup-green-dark rounded-2xl px-6 py-3 font-semibold disabled:opacity-60">
                {aiLoading ? "Thinking…" : "Recommend"}
              </button>
            </div>
            {aiPicks.length > 0 && (
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {aiPicks.map((r) => (
                  <Link key={r.restaurant_id} to={`/restaurant/${r.restaurant_id}`} data-testid={`ai-pick-${r.restaurant_id}`}
                    className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-eatup-green/40 transition">
                    <div className="relative h-24 overflow-hidden">
                      <img src={r.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                    </div>
                    <div className="p-3">
                      <div className="font-semibold text-sm">{r.name}</div>
                      <div className="text-[11px] text-white/60 line-clamp-2 mt-0.5">{r.reason}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sponsored horizontal rail */}
      {sponsored.length > 0 && (
        <HorizontalRail
          title="Featured on Eats GND"
          eyebrow="Sponsored"
          icon={<Flame className="w-4 h-4" />}
          restaurants={sponsored}
          navigate={navigate}
          large
          idPrefix="sponsored"
        />
      )}

      {/* Popular */}
      <HorizontalRail title="Popular near you" eyebrow="Trending" restaurants={popular} navigate={navigate} idPrefix="popular" />

      {/* Fastest */}
      <HorizontalRail title="Fastest delivery" eyebrow="Under 30 min" restaurants={fastest} navigate={navigate} idPrefix="fastest" />

      {/* All restaurants grid */}
      <section className="mt-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-2xl sm:text-3xl font-bold">
            {category === "All" ? "All restaurants" : `${category} spots`}
          </h2>
          <div className="text-sm text-neutral-500">{filtered.length} results</div>
        </div>
        {loading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-3xl bg-white p-3">
                <div className="skeleton h-44 rounded-2xl" />
                <div className="skeleton h-4 mt-4 w-2/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => (
              <RestaurantCard key={r.restaurant_id} r={r} navigate={navigate} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ReorderLastBanner({ order, onReorder }) {
  const items = order.items || [];
  const preview = items.slice(0, 2).map((i) => i.name).join(", ") + (items.length > 2 ? ` +${items.length - 2} more` : "");
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-6">
      <div
        className="w-full rounded-3xl bg-white card-shadow overflow-hidden flex items-center gap-4 p-4"
        data-testid="reorder-last-banner"
      >
        <img src={order.restaurant_image} alt={order.restaurant_name} className="w-16 h-16 rounded-2xl object-cover shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.35em] font-black text-eatup-green flex items-center gap-1.5">
            <RotateCcw className="w-3 h-3" /> Order it again
          </div>
          <div className="mt-1 font-display font-bold text-lg leading-tight truncate">{order.restaurant_name}</div>
          <div className="text-xs text-neutral-500 truncate">{preview}</div>
        </div>
        <button
          onClick={onReorder}
          data-testid="reorder-last-btn"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-eatup-green hover:bg-eatup-green-dark text-white text-xs font-black uppercase tracking-widest px-4 sm:px-5 py-3 shadow-md transition active:scale-[0.98]"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Reorder
        </button>
      </div>
    </div>
  );
}

function FavoritesRail({ favorites, onAdd, navigate }) {
  return (
    <section className="mt-8 max-w-7xl mx-auto pl-5 sm:pl-8" data-testid="favorites-rail">
      <div className="pr-5 sm:pr-8 mb-4 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="uppercase tracking-[0.3em] text-[10px] text-eatup-green font-bold flex items-center gap-1.5">
            <Heart className="w-3.5 h-3.5 fill-current" /> Your Favorites
          </div>
          <h2 className="font-display text-2xl font-bold mt-1">Order what you love again</h2>
        </div>
      </div>
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 pr-5 sm:pr-8">
        {favorites.map((f) => (
          <div
            key={f.item_id}
            data-testid={`favorite-card-${f.item_id}`}
            className="shrink-0 w-64 rounded-3xl overflow-hidden bg-white card-shadow"
          >
            <button
              onClick={() => navigate(`/restaurant/${f.restaurant_id}`)}
              className="relative w-full h-36 overflow-hidden text-left group"
            >
              <img src={f.image} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
              <div className="absolute top-2 left-2 bg-eatup-charcoal/90 backdrop-blur text-eatup-yellow text-[9px] uppercase tracking-widest font-black px-2 py-1 rounded-full flex items-center gap-1">
                <Heart className="w-2.5 h-2.5 fill-current" /> Ordered {f.times_ordered}×
              </div>
            </button>
            <div className="p-4">
              <div className="font-display font-bold truncate">{f.name}</div>
              <div className="text-[11px] text-neutral-500 truncate mt-0.5">{f.restaurant_name}</div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="font-display font-black text-lg tabular text-eatup-charcoal">${f.price.toFixed(2)}</div>
                <button
                  onClick={() => onAdd(f)}
                  data-testid={`favorite-add-${f.item_id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-eatup-green hover:bg-eatup-green-dark text-white text-xs font-black uppercase tracking-widest px-3.5 py-2 shadow-sm transition active:scale-95"
                  aria-label={`Add ${f.name} to cart`}
                >
                  <Plus className="w-3.5 h-3.5" /> Add
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ActiveTrackBanner({ order, navigate }) {
  // Prominent, hard-to-miss banner that drops the customer into full-screen
  // live tracking. It flashes a status-appropriate accent and shows a mini
  // status line so the customer knows what stage the food is in.
  const stage = (() => {
    switch (order.status) {
      case "placed":     return { chip: "Order placed",   line: "The kitchen has your order",    accent: "from-orange-500 to-orange-600" };
      case "preparing":  return { chip: "Cooking",        line: "The chefs are on it",           accent: "from-orange-500 to-amber-500" };
      case "ready":      return { chip: "Ready",          line: "Waiting for a driver",          accent: "from-amber-400 to-eatup-yellow" };
      case "picked_up":  return { chip: "Picked up",      line: "Food is on the move",           accent: "from-sky-500 to-cyan-500" };
      case "on_the_way": return { chip: "On the way",     line: `Driver is heading to you`,      accent: "from-cyan-500 to-eatup-green" };
      default:           return { chip: order.status,     line: "",                              accent: "from-eatup-green to-emerald-500" };
    }
  })();
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-6">
      <button
        onClick={() => navigate(`/track/${order.order_id}`)}
        data-testid="home-track-banner"
        className={`group relative w-full rounded-3xl overflow-hidden text-left bg-eatup-charcoal text-white shadow-2xl border border-white/10 hover:border-eatup-green/50 transition active:scale-[0.995] focus:outline-none focus:ring-4 focus:ring-eatup-green/30`}
      >
        {/* Colored top strip that pulses subtly. */}
        <div className={`h-2 bg-gradient-to-r ${stage.accent}`} />
        <div className="relative p-5 sm:p-6 flex items-center gap-4 sm:gap-5">
          {/* Live radar icon */}
          <div className="relative w-14 h-14 rounded-2xl bg-eatup-green/15 flex items-center justify-center shrink-0">
            <span className="absolute inset-2 rounded-full bg-eatup-green/30 animate-ping" />
            <span className="relative w-3 h-3 rounded-full bg-eatup-green shadow-[0_0_10px_rgba(255,106,26,0.7)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.35em] font-black text-eatup-yellow flex items-center gap-2">
              Live · {stage.chip}
            </div>
            <div className="mt-1 font-display font-black text-xl sm:text-2xl leading-tight truncate">
              Track your food
            </div>
            <div className="mt-0.5 text-sm text-neutral-400 truncate">
              {stage.line} · <span className="text-neutral-200 font-semibold">{order.restaurant_name}</span>
              {order.eta_minutes ? <> · <span className="text-eatup-green font-bold">{order.eta_minutes} min ETA</span></> : null}
            </div>
          </div>
          <div className="hidden sm:flex flex-col items-end">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Order #</div>
            <div className="font-mono font-bold text-lg text-white">#{order.order_id.slice(-6).toUpperCase()}</div>
          </div>
          <div className="w-11 h-11 rounded-full bg-eatup-green group-hover:bg-eatup-green-dark text-white flex items-center justify-center shrink-0 shadow-lg transition">
            <ChevronRight className="w-5 h-5" />
          </div>
        </div>
      </button>
    </div>
  );
}

function AiRecoRail({ picks, loading, personalized, navigate }) {
  if (!loading && !picks?.length) return null;
  const heading = personalized ? "Recommended for you" : "Fresh picks for tonight";
  return (
    <section className="mt-8 max-w-7xl mx-auto pl-5 sm:pl-8" data-testid="ai-reco-rail">
      <div className="pr-5 sm:pr-8 mb-4 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="uppercase tracking-[0.3em] text-[10px] text-eatup-green font-bold flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> AI curated {personalized && <span className="text-eatup-yellow">· personalized</span>}
          </div>
          <h2 className="font-display text-2xl font-bold mt-1" data-testid="ai-reco-heading">{heading}</h2>
        </div>
      </div>
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 pr-5 sm:pr-8">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="shrink-0 w-72 rounded-3xl bg-white p-3" data-testid="ai-reco-skeleton">
              <div className="skeleton h-40 rounded-2xl" />
              <div className="skeleton h-4 mt-3 w-2/3" />
              <div className="skeleton h-3 mt-2 w-11/12" />
            </div>
          ))
        ) : (
          picks.map((r) => (
            <button
              key={r.restaurant_id}
              data-testid={`ai-reco-card-${r.restaurant_id}`}
              onClick={() => navigate(`/restaurant/${r.restaurant_id}`)}
              className="group shrink-0 w-72 rounded-3xl overflow-hidden bg-white card-shadow card-shadow-hover text-left relative"
            >
              <div className="relative h-40 overflow-hidden">
                <img src={r.image_url} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                <div className="absolute top-3 left-3 bg-eatup-charcoal/90 backdrop-blur text-eatup-yellow text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Pick
                </div>
                <div className="absolute top-3 right-3 bg-eatup-green text-white rounded-full w-9 h-9 flex items-center justify-center text-sm font-bold shadow-lg">
                  {r.rating}
                </div>
              </div>
              <div className="p-4">
                <div className="font-display font-bold leading-tight truncate">{r.name}</div>
                <div className="text-[11px] text-eatup-green font-semibold italic mt-1 line-clamp-2" data-testid={`ai-reco-reason-${r.restaurant_id}`}>
                  “{r.reason}”
                </div>
                <div className="mt-2.5 flex items-center gap-3 text-[11px] text-neutral-600">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.eta_minutes} min</span>
                  <span>·</span>
                  <span>${r.delivery_fee?.toFixed(2)} delivery</span>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </section>
  );
}

function CategoryTile({ label, emoji, image, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex flex-col items-center gap-2 shrink-0 group">
      <div className={`relative w-20 h-20 rounded-full overflow-hidden ring-2 transition ${active ? "ring-eatup-green" : "ring-transparent"}`}>
        {image ? (
          <img src={image} alt={label} className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-eatup-green to-eatup-green-dark flex items-center justify-center text-3xl">{emoji}</div>
        )}
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute bottom-1 right-1 text-xl">{emoji}</div>
      </div>
      <div className={`text-xs font-semibold ${active ? "text-eatup-green" : "text-eatup-charcoal"}`}>{label}</div>
    </button>
  );
}

function DealCard({ title, code, color, icon, image, testid }) {
  return (
    <div data-testid={testid} className={`relative rounded-3xl overflow-hidden h-32 sm:h-36 bg-gradient-to-br ${color} text-white p-5 flex flex-col justify-between`}>
      <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="relative flex items-center gap-2 text-xs uppercase tracking-widest font-bold">
        {icon} Promo
      </div>
      <div className="relative">
        <div className="font-display text-lg font-bold leading-tight">{title}</div>
        <div className="mt-1 text-xs font-mono bg-white/15 backdrop-blur w-fit px-2 py-0.5 rounded">{code}</div>
      </div>
    </div>
  );
}

function HorizontalRail({ title, eyebrow, icon, restaurants, navigate, large, idPrefix }) {
  if (!restaurants?.length) return null;
  return (
    <section className="mt-10 max-w-7xl mx-auto pl-5 sm:pl-8">
      <div className="pr-5 sm:pr-8 flex items-baseline justify-between mb-4">
        <div>
          <div className="uppercase tracking-[0.3em] text-[10px] text-eatup-green font-bold flex items-center gap-1.5">
            {icon}{eyebrow}
          </div>
          <h2 className="font-display text-2xl font-bold mt-1">{title}</h2>
        </div>
      </div>
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 pr-5 sm:pr-8">
        {restaurants.map((r) => (
          <div key={r.restaurant_id} className={`shrink-0 ${large ? "w-80" : "w-64"}`}>
            <RestaurantCard r={r} navigate={navigate} compact={!large} idPrefix={idPrefix} />
          </div>
        ))}
      </div>
    </section>
  );
}

function RestaurantCard({ r, navigate, compact, idPrefix }) {
  const testid = idPrefix ? `${idPrefix}-restaurant-card-${r.restaurant_id}` : `restaurant-card-${r.restaurant_id}`;
  const closed = r.is_open === false;
  const milesLabel = formatMiles(r.miles);
  return (
    <button
      onClick={() => navigate(`/restaurant/${r.restaurant_id}`)}
      data-testid={testid}
      className={`group rounded-3xl overflow-hidden bg-white card-shadow card-shadow-hover text-left w-full ${closed ? "opacity-90" : ""}`}
    >
      <div className={`relative overflow-hidden ${compact ? "h-36" : "h-48"}`}>
        <img src={r.image_url} alt={r.name} className={`w-full h-full object-cover group-hover:scale-105 transition duration-700 ${closed ? "grayscale" : ""}`} />
        {closed && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <div className="bg-red-500 text-white text-[11px] uppercase tracking-widest font-black px-3 py-1.5 rounded-full shadow-lg" data-testid={`closed-badge-${r.restaurant_id}`}>
              Closed · Opens {r.opens_at || "9:00"}
            </div>
          </div>
        )}
        {r.sponsored && !closed && (
          <div className="absolute top-3 left-3 bg-eatup-yellow text-eatup-charcoal text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full">
            Sponsored
          </div>
        )}
        {milesLabel && !closed && (
          <div className="absolute top-3 left-3 bg-eatup-charcoal/90 backdrop-blur text-eatup-yellow text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded-full" data-testid={`distance-${r.restaurant_id}`}>
            {milesLabel} away
          </div>
        )}
        <div className="absolute top-3 right-3 bg-eatup-green text-white rounded-full w-9 h-9 flex items-center justify-center text-sm font-bold shadow-lg">
          {r.rating}
        </div>
      </div>
      <div className="p-4">
        <div className="font-display font-bold leading-tight truncate">{r.name}</div>
        <div className="text-xs text-neutral-500 mt-0.5 truncate">{r.categories?.join(" · ")}</div>
        <div className="mt-3 flex items-center gap-3 text-xs text-neutral-600">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.eta_minutes} min</span>
          <span>·</span>
          <span>${r.delivery_fee?.toFixed(2)} delivery</span>
          {milesLabel && (<><span>·</span><span className="text-eatup-green font-bold">{milesLabel}</span></>)}
        </div>
      </div>
    </button>
  );
}
